import Dictionary from "../Dictionary";
import Boards from "../database/Boards";
import Edges from "../database/Edges";
import IBoardData from "../database/IBoardData";
import IEdgeData from "../database/IEdgeData";
import IParamScriptsRule from "../database/IParamScriptsRule";
import ComboBox from "../common/ComboBox";
import { CDN_BASE_URL} from "../config/cdn";
import { createCdnThumbnailUrl } from "../config/cdn";
import { ZERO_GUID as _ZERO_GUID } from "../v2/configurator/cards/edgeKeys";

class BoardProduct {

    private boardCombo: ComboBox;
    
    private target: HTMLElement;
    private boardThumbnailImg: HTMLImageElement;
    private duplungThumbnailImg: HTMLImageElement;
    
    // TODO: These should not be public
    public duplungCombo: ComboBox;
    public boardGrainCombo: ComboBox;
    public duplungGrainCombo: ComboBox;
    
    public BoardSelectorCombo: HTMLDivElement;
    public DuplungSelectorCombo: HTMLDivElement;
    public BoardGrainSelectorCombo: HTMLElement;
    public DuplungGrainSelectorCombo: HTMLElement;
    
    // TODO: These should also not be public, use getters and setters
    public boardID: string;
    public duplungID: string;
    public duplungEnabled: boolean;
    public edgebandable: boolean;

    constructor(boardID: string, target: HTMLElement) {

        if (boardID == null) {
            throw "BoardProduct constructor must be initialized with valid BoardID, got: " + boardID;
        }

        this.target = target;
        this.boardID = boardID;
        this.duplungID = boardID;
        this.duplungEnabled = false;
        this.edgebandable = false;

        const theBoard: IBoardData | null = Boards.instance!.findByID(this.boardID);
        const theDuplung: IBoardData | null = Boards.instance!.findByID(this.duplungID);

        // clone BoardProduct from HTML template
        const boardProductTemplate: HTMLElement = document.querySelector<HTMLElement>('[data-id="BoardProduct"]')!;
        const clonedBoardProduct: HTMLElement = boardProductTemplate.cloneNode(true) as HTMLElement;

        // clone DuplungProduct from HTML template
        const duplungProductTemplate: HTMLElement = document.querySelector<HTMLElement>('[data-id="DuplungProduct"]')!;
        const clonedDuplungProduct: HTMLElement = duplungProductTemplate.cloneNode(true) as HTMLElement;

        // create thumbnail references for both
        this.boardThumbnailImg = clonedBoardProduct.querySelector<HTMLImageElement>('[data-id="thumbnail"]')!;
        this.duplungThumbnailImg = clonedDuplungProduct.querySelector<HTMLImageElement>('[data-id="thumbnail"]')!;

        // setup board selector ComboBox
        this.BoardSelectorCombo = clonedBoardProduct.querySelector<HTMLDivElement>('[data-id="board-selector-combo"]')!;

        // setup duplung selector ComboBox (duplung is hidden by default)
        this.DuplungSelectorCombo = clonedDuplungProduct.querySelector<HTMLDivElement>('[data-id="board-selector-combo"]')!;

        // setup board selector ComboBox
        // update combobox to handle capital ID and stuff
        this.BoardSelectorCombo.setAttribute(
            "data-json",
            JSON.stringify(Boards.instance!.children())
        );

        // setup duplung selector ComboBox
        // update combobox to handle capital ID and stuff
        this.DuplungSelectorCombo.setAttribute(
            "data-json",
            JSON.stringify(Boards.instance!.children().filter(board => { return board.Duplungable == 1; }))
        );

        // init board combo
        this.boardCombo = new ComboBox();
        this.boardCombo.init(this.BoardSelectorCombo);

        this.sortComboDataByName(this.boardCombo);
        this.extendComboData(this.boardCombo); // adds DisplayName and CaptionDisplayName to the combo data

        this.boardCombo.selectByID(this.boardID);
        this.updateThumbnails();

        // init duplung combo
        this.duplungCombo = new ComboBox();
        this.duplungCombo.init(this.DuplungSelectorCombo);
        this.extendComboData(this.duplungCombo); // adds DisplayName and CaptionDisplayName to the combo data
        if (theBoard?.Duplungable == 1) {
            this.duplungCombo.selectByID(this.duplungID);
            this.updateThumbnails();
        }

        // setup grain combo for board
        this.BoardGrainSelectorCombo = clonedBoardProduct.querySelector<HTMLElement>('[data-id="grain-selector-combo"]')!;
        this.BoardGrainSelectorCombo.setAttribute("data-json", '[ { "ID":"Straight","Name":"Egyenes" }, { "ID":"Cross","Name":"Kereszt" }, { "ID":"DoNotCare","Name":"Nem számít" } ]')
        this.boardGrainCombo = new ComboBox();
        this.boardGrainCombo.init(this.BoardGrainSelectorCombo);
        this.boardGrainCombo.selectByID("Straight");

        if (theBoard?.Oriented == 0) {
            this.BoardGrainSelectorCombo.classList.add("hidden");
        }

        // setup grain combo for duplung
        this.DuplungGrainSelectorCombo = clonedDuplungProduct.querySelector<HTMLElement>('[data-id="grain-selector-combo"]')!;
        this.DuplungGrainSelectorCombo.setAttribute("data-json", '[ { "ID":"Straight","Name":"Egyenes" }, { "ID":"Cross","Name":"Kereszt" }, { "ID":"DoNotCare","Name":"Nem számít" } ]')
        this.duplungGrainCombo = new ComboBox();
        this.duplungGrainCombo.init(this.DuplungGrainSelectorCombo);
        this.duplungGrainCombo.selectByID("Straight");

        if (theDuplung?.Oriented == 0) {
            this.DuplungGrainSelectorCombo.classList.add("hidden");
        }

        target.appendChild(clonedBoardProduct);
        target.appendChild(clonedDuplungProduct);
    }

    private sortComboDataByName(combo: ComboBox): void {
        if (combo.data != null) {
            combo.data.sort((a, b) => {
                return a.Name.localeCompare(b.Name);
            });
        }
    }

    // extends combo data with DisplayName and CaptionDisplayName
    private extendComboData(combo: ComboBox): void {
        // update combo elements by creating uniquely formatted display names
        if (combo.data != null) {
            for(let row of combo.data) {
                row.CaptionDisplayName = row.Name + " - " + row.SheetThickness + " mm";
                if(row.UnitPrice == null) {
                    const unitPrice = Math.round(row.SheetPrice! / (row.SheetWidth! / 1000 * row.SheetHeight! / 1000));
                    row.DisplayName = row.Name + " - " + row.SheetThickness + " mm (" + row.SheetPrice + " Ft/tábla &rarr; ~" + unitPrice + " Ft/m&sup2;)";
                } else {
                    row.DisplayName = row.Name + " - " + row.SheetThickness + " mm (" + row.UnitPrice + " Ft/m&sup2;)";
                }
            }
        }
    }

    public updateUIElementsVisibility(): void {
        const theBoard: IBoardData | null = Boards.instance!.findByID(this.boardID);

        if(theBoard != null) {
            // hide the duplung button if the board is not duplungable
            this.updateDuplungButtonVisibility(theBoard.Duplungable);

            // hide the edge part if the board is not duplungable
            this.updateEdgeConfigurationVisibility(theBoard.EdgeBandable);
        }
    }

    public updateDuplungButtonVisibility(duplungable: number): void {
        if (duplungable == 1) {
            this.target.querySelector<HTMLElement>('[data-id="duplung-btn"]')!.classList.remove("hidden");
        } else {
            this.target.querySelector<HTMLElement>('[data-id="duplung-btn"]')!.classList.add("hidden");
            this.duplungEnabled = false;

            const duplungProduct: HTMLElement = this.target.querySelector<HTMLElement>('[data-id="DuplungProduct"]')!;
            const duplungButton: HTMLImageElement = this.target.querySelector<HTMLImageElement>('[data-id="duplung-btn"]')!;

            duplungProduct.classList.add("hidden");
            duplungButton.src = "/modules/ubercabinet/views/images/duplung.svg";
        }
    }

    private updateEdgeConfigurationVisibility(edgeBandable: number): void {
        if (edgeBandable == 1) {
            this.target.querySelector<HTMLElement>('[data-id="outer-container"]')!.classList.remove("hidden");
            this.edgebandable = true;
        } else {
            this.target.querySelector<HTMLElement>('[data-id="outer-container"]')!.classList.add("hidden");
            this.edgebandable = false;
        }
    }

    public updateThumbnails(): void {
        const theBoard: IBoardData | null = Boards.instance!.findByID(this.boardID);

        if (theBoard != null) {
            this.boardThumbnailImg.src = "/modules/ubercabinet/views/thumbnails/" + theBoard.ProductImage + ".png";
            this.boardThumbnailImg.alt = theBoard.ProductImage;
            // TODO: uncomment this to use CDN urls.
            // this.boardThumbnailImg.src = createCdnThumbnailUrl(theBoard.ProductImage, 256);
            // this.boardThumbnailImg.alt = theBoard.ProductImage;
        }

        const theDuplung: IBoardData | null = Boards.instance!.findByID(this.duplungID);
        if (theDuplung != null) {
            this.duplungThumbnailImg.src = "/modules/ubercabinet/views/thumbnails/" + theDuplung.ProductImage + ".png";
            this.duplungThumbnailImg.alt = theDuplung.ProductImage;
            // this.duplungThumbnailImg.src = createCdnThumbnailUrl(theDuplung.ProductImage, 256);
            // this.duplungThumbnailImg.alt = theDuplung.ProductImage;
        }
    }
}

type EdgesDescriptor = { [id: string]: ['T' | 'B' | 'L' | 'R'] };

class EdgeProduct {
    
    private cloned: HTMLElement;
    
    private edgeThumbnailImg: HTMLImageElement;
    
    // TODO: These should not be public
    public edgeID: string;
    
    public combo: ComboBox;
    
    public DecorSelectorCombo: HTMLElement;
    public TrashBtn: HTMLButtonElement;

    constructor(edges: EdgesDescriptor, edgeID: string) {
        this.edgeID = edgeID;

        const templates: HTMLElement = document.querySelector<HTMLElement>('[data-id="templates"]')!;
        const template: HTMLElement = templates.querySelector<HTMLElement>('[data-id="EdgeProduct"]')!;
        this.cloned = template.cloneNode(true) as HTMLElement;

        this.edgeThumbnailImg = this.cloned.querySelector<HTMLImageElement>('[data-id="thumbnail"]')!;
        this.updateThumbnail();

        // setup edge decor selector ComboBox
        this.DecorSelectorCombo = this.cloned.querySelector<HTMLElement>('[data-id="edge-selector-combo"]')!;

        // update combobox to handle capital ID and stuff
        this.DecorSelectorCombo.setAttribute(
            "data-json",
            JSON.stringify(Edges.instance!.children())
        );

        this.combo = new ComboBox();
        this.combo.init(this.DecorSelectorCombo);

        // update combo elements by creating uniquely formatted display names
        for(let row of this.combo.data!) {
            row.CaptionDisplayName = row.Name + " - " + row.Thickness + " mm";
            row.DisplayName = row.Name + " - " + row.Thickness + " mm (" + row.UnitPrice + " Ft/m)";
        }

        // if there are explicitly selected edges, we need to select the checkboxes
        if (Object.keys(edges).length > 0) {
            // select the appropriate checkbox
            if (edges[edgeID]) {
                for (let letter of edges[edgeID]) {
                    const cbHTML = this.cloned.querySelector<HTMLInputElement>('[data-id="' + letter + '"]')!;
                    if(edgeID === MaterialConfigurator.ZERO_GUID) {
                        cbHTML.checked = false;
                    } else {
                        cbHTML.checked = true;
                    }
                }
            }
            if (edgeID === MaterialConfigurator.ZERO_GUID) {
                console.log("EdgeProduct: edgeID is invalid, but there are edges selected: " + JSON.stringify(edges));
            }
        }
        // If the edge id is valid, select the edge material combobox
        if (edgeID !== MaterialConfigurator.ZERO_GUID) {
            this.combo.selectByID(edgeID);
        }

        this.TrashBtn = this.cloned.querySelector<HTMLButtonElement>('[data-id="trash-btn"]')!;
    }

    // updates thumbnail based on current edgeID
    public updateThumbnail(): void {
        const theEdge: IEdgeData | null = Edges.instance!.findByID(this.edgeID);
        if (theEdge != null) {
            this.edgeThumbnailImg.src = "/modules/ubercabinet/views/thumbnails/" + theEdge.ProductImage + ".png";
            this.edgeThumbnailImg.alt = theEdge.ProductImage;
        }
    }

    public removeHTMLElement(): void {
        this.cloned.remove();
    }

    public getHTMLElement(): HTMLElement {
        return this.cloned;
    }
}

class MaterialConfigurator {

    public static readonly ZERO_GUID: string = _ZERO_GUID;

    public rule: IParamScriptsRule;

    private target: HTMLElement;
    private container: HTMLElement;

    private boardProduct: BoardProduct;
    private edgeProducts: EdgeProduct[];

    private setParameters: () => void;

    constructor(rule: IParamScriptsRule, setParameters: () => void) {
        if (!rule.parameterValue.hasOwnProperty("BoardProduct")) {
            throw "MaterialConfigurator's rule.parameterValue must have a BoardProduct property, but: " + JSON.stringify(rule.parameterValue);
        }

        this.rule = rule;
        this.setParameters = setParameters;

        switch (rule.tab) {
            case "configuration": this.target = document.querySelector<HTMLElement>('[data-id="configuration"]')!; break;
            case "material":
            case "materials": this.target = document.querySelector<HTMLElement>('[data-id="material"]')!; break;
            default: this.target = document.querySelector<HTMLElement>('[data-id="material"]')!; break;
        }

        const decorGroup: HTMLDivElement = document.createElement("div");
        decorGroup.classList.add("decor");
        decorGroup.setAttribute("data-id", "decor-group");
        this.target.appendChild(decorGroup);

        const h2Element: HTMLHeadingElement = document.createElement("h2");
        h2Element.classList.add("flex-center");
        h2Element.classList.add("gap05rem");
        h2Element.innerHTML = Dictionary.get(rule.title) + '<img class="duplung-btn" data-id="duplung-btn" title="Duplung" height="40px" src="/modules/ubercabinet/views/images/duplung.svg" />';
        decorGroup.appendChild(h2Element);

        h2Element.querySelector<HTMLButtonElement>('[data-id="duplung-btn"]')!.addEventListener('click', (event: Event) => {
            const duplungProduct: HTMLElement = decorGroup.querySelector<HTMLElement>('[data-id="DuplungProduct"]')!;
            const duplungButton: HTMLImageElement = h2Element.querySelector<HTMLImageElement>('[data-id="duplung-btn"]')!;

            if (this.boardProduct.duplungEnabled === false) {
                this.boardProduct.duplungEnabled = true;
                duplungProduct.classList.remove("hidden");
                duplungButton.src = "/modules/ubercabinet/views/images/duplung_selected.svg";

                // as soon as we turn on duplung, we need to create DuplungProduct in the rules
                this.boardProduct.duplungCombo.sendEvent(); // so this will trigger a changed event and set this.rule.value.DuplungProduct

                for (const ep of this.edgeProducts) {
                    this.hideNormalBoardEdges(ep.combo);
                }

            } else {
                this.boardProduct.duplungEnabled = false;
                duplungProduct.classList.add("hidden");
                duplungButton.src = "/modules/ubercabinet/views/images/duplung.svg";
                delete this.rule.parameterValue.DuplungProduct;
                delete this.rule.parameterValue.DuplungPatternDirection;

                for (const ep of this.edgeProducts) {
                    this.hideDuplungBoardEdges(ep.combo);
                }

                setParameters();
            }
        });

        const boardProduct: IBoardData | null = Boards.instance!.findByID(rule.parameterValue.BoardProduct);
        // Fallback to the first valid board if the board is not found
        if (boardProduct == null) {
            const firstBoard: IBoardData | null = Boards.instance!.first();
            rule.parameterValue.BoardProduct = firstBoard ? firstBoard.ID : MaterialConfigurator.ZERO_GUID;
        }

        this.boardProduct = new BoardProduct(rule.parameterValue.BoardProduct, decorGroup);

        // board combo change
        this.boardProduct.BoardSelectorCombo.addEventListener("changed", (e: Event) => {
            const event = e as CustomEvent<{ value: string }>;
            const boardID: string = event.detail.value;
            this.boardProduct.boardID = boardID;
            this.rule.parameterValue.BoardProduct = boardID;

            // change the duplung combo to the same board
            this.boardProduct.duplungCombo.selectByID(boardID);

            const theBoard: IBoardData | null = Boards.instance!.findByID(boardID);
            if (theBoard?.Oriented == 1) {
                this.boardProduct.BoardGrainSelectorCombo.classList.remove("hidden");
                this.boardProduct.boardGrainCombo.sendEvent();
            } else {
                this.boardProduct.BoardGrainSelectorCombo.classList.add("hidden");
                delete this.rule.parameterValue.BoardPatternDirection;
            }

            this.boardProduct.updateThumbnails();

            // this will update duplung button visibility and edge configuration visibility
            this.boardProduct.updateUIElementsVisibility();

            // use compatible edgings of the board to update the combo
            if (theBoard != null && theBoard.CompatibleEdgings != null) {
                for (const ep of this.edgeProducts) {
                    const numOfCompatElements : number = ep.combo.rearrange(theBoard.CompatibleEdgings);
                    ep.combo.selectByIndex(0);
                    ep.combo.setSeparatorAt(numOfCompatElements);
                }
            }

            setParameters();
        });

        // board grain combo change
        this.boardProduct.BoardGrainSelectorCombo.addEventListener("changed", (e: Event) => {
            const event = e as CustomEvent<{ value: string }>;
            const boardGrain: string = event.detail.value;
            console.log("board grain combo value changed: " + boardGrain);
            this.rule.parameterValue.BoardPatternDirection = boardGrain;
            setParameters();
        });
        // restore board grain combo value from the rule: BoardPatternDirection
        if (this.rule.parameterValue.hasOwnProperty("BoardPatternDirection")) {
            this.boardProduct.boardGrainCombo.selectByID(this.rule.parameterValue.BoardPatternDirection);
        }
        this.boardProduct.boardGrainCombo.sendEvent();

        // duplung combo change
        this.boardProduct.DuplungSelectorCombo.addEventListener("changed", (e: Event) => {
            const event = e as CustomEvent<{ value: string }>;
            const duplungID: string = event.detail.value;
            this.boardProduct.duplungID = duplungID;

            this.rule.parameterValue.DuplungProduct = duplungID;
            const theDuplung: IBoardData | null = Boards.instance!.findByID(duplungID);

            if (theDuplung?.Oriented == 1) {
                this.boardProduct.DuplungGrainSelectorCombo.classList.remove("hidden");
                this.boardProduct.duplungGrainCombo.sendEvent();
            } else {
                this.boardProduct.DuplungGrainSelectorCombo.classList.add("hidden");
                delete this.rule.parameterValue.DuplungPatternDirection;
            }

            this.boardProduct.updateThumbnails();
            setParameters();
        });
        // restore duplung state if available
        if (this.rule.parameterValue.hasOwnProperty("DuplungProduct")) {
            // restore duplung product
            this.boardProduct.duplungCombo.selectByID(this.rule.parameterValue.DuplungProduct);
            this.boardProduct.duplungCombo.sendEvent();
            // resotre duplung grain
            this.boardProduct.duplungGrainCombo.selectByID(this.rule.parameterValue.DuplungPatternDirection);
            this.boardProduct.duplungGrainCombo.sendEvent();
            // initiate click event on the duplung button
            h2Element.querySelector<HTMLButtonElement>('[data-id="duplung-btn"]')!.click();
        }

        // duplung grain combo change
        this.boardProduct.DuplungGrainSelectorCombo.addEventListener("changed", (e: Event) => {
            const event = e as CustomEvent<{ value: string }>;
            const duplungGrain: string = event.detail.value;
            console.log("duplung grain combo value changed: " + duplungGrain);
            this.rule.parameterValue.DuplungPatternDirection = duplungGrain;
            setParameters();
        });

        const outerContainer: HTMLDivElement = document.createElement("div");
        outerContainer.classList.add("outer-container");
        outerContainer.setAttribute("data-id", "outer-container");
        decorGroup.appendChild(outerContainer);

        this.container = document.createElement("div");
        this.container.setAttribute("data-id", "edges-container");
        outerContainer.appendChild(this.container);

        const addEdgeProductSlotBtn: HTMLButtonElement = document.createElement("button");
        addEdgeProductSlotBtn.classList.add("add-slot-btn");
        addEdgeProductSlotBtn.innerHTML = '<span title="Másik élzáró anyag hozzáadása" class="material-symbols-outlined">add</span>';

        addEdgeProductSlotBtn.addEventListener("click", (event: Event) => {
            this.addEdgeProductSlot();
        });

        outerContainer.appendChild(addEdgeProductSlotBtn);

        this.edgeProducts = [];

        this.addEdgeProduct(this.rule);

        for (const ep of this.edgeProducts) {
            this.hideDuplungBoardEdges(ep.combo);

            const theBoard: IBoardData | null = Boards.instance!.findByID(this.boardProduct.boardID);
            if (theBoard != null && theBoard.CompatibleEdgings != null) {
                const numOfCompatElements : number = ep.combo.rearrange(theBoard.CompatibleEdgings);
                ep.combo.setSeparatorAt(numOfCompatElements);
            }
        }

        // this will update duplung button visibility and edge configuration visibility
        this.boardProduct.updateUIElementsVisibility();

    } // end of MaterialConfigurator's constructor

    // modifies combo data from outside
    private hideDuplungBoardEdges(combo: ComboBox): void {
        for (const row of combo.data!) {
            if(row.Width! > 30) {
                row.Hidden = true;
            } else {
                row.Hidden = false;
            }
        }
    }

    // modified combo data from outside
    private hideNormalBoardEdges(combo: ComboBox): void {
        for (const row of combo.data!) {
            if(row.Width! <= 30) {
                row.Hidden = true;
            } else {
                row.Hidden = false;
            }
        }
    }

    public cleanup(): void {
        const templates: NodeListOf<HTMLElement> = this.target.querySelectorAll<HTMLElement>('[data-id="decor-group"]');
        for (let template of templates) {
            template.remove();
        }
    }

    private addEdgeProduct(rule: IParamScriptsRule): void {
        const edgeProductTypes: string[] = ["EdgeProduct", "LeftEdgeProduct", "RightEdgeProduct", "TopEdgeProduct", "BottomEdgeProduct"];
        if ( ! edgeProductTypes.some(productType => rule.parameterValue.hasOwnProperty(productType))) {
            throw "Expected one of " + edgeProductTypes.join(", ") + " to be present, but received: " + rule.parameterValue;
        }

        console.log("Adding edge product");
        console.log("title: " + rule.title); // BoardMaterial, BaseMaterial, ...
        console.log("rule.parameterValue.EdgeProduct: " + rule.parameterValue.EdgeProduct)

        // if EdgeProduct is set, then all edges are the same
        if (rule.parameterValue.hasOwnProperty("EdgeProduct")) {
            const edgeProduct: IEdgeData | null = Edges.instance!.findByID(rule.parameterValue.EdgeProduct);
            // assign ZERO_GUID if the edge is not found
            if (edgeProduct == null) {
                rule.parameterValue.EdgeProduct = MaterialConfigurator.ZERO_GUID;
            }

            rule.parameterValue.LeftEdgeProduct = rule.parameterValue.EdgeProduct;
            rule.parameterValue.RightEdgeProduct = rule.parameterValue.EdgeProduct;
            rule.parameterValue.TopEdgeProduct = rule.parameterValue.EdgeProduct;
            rule.parameterValue.BottomEdgeProduct = rule.parameterValue.EdgeProduct;

            // remove EdgeProduct from the rule
            delete rule.parameterValue.EdgeProduct;

        } else { // assign ZERO_GUID if the edge is not found in any of the edge products
            if (rule.parameterValue.hasOwnProperty("LeftEdgeProduct")) {
                const leftEdgeProduct: IEdgeData | null = Edges.instance!.findByID(rule.parameterValue.LeftEdgeProduct);
                if (leftEdgeProduct == null) {
                    rule.parameterValue.LeftEdgeProduct = MaterialConfigurator.ZERO_GUID;
                }
            }
            if (rule.parameterValue.hasOwnProperty("RightEdgeProduct")) {
                const rightEdgeProduct: IEdgeData | null = Edges.instance!.findByID(rule.parameterValue.RightEdgeProduct);
                if (rightEdgeProduct == null) {
                    rule.parameterValue.RightEdgeProduct = MaterialConfigurator.ZERO_GUID;
                }
            }
            if (rule.parameterValue.hasOwnProperty("TopEdgeProduct")) {
                const topEdgeProduct: IEdgeData | null = Edges.instance!.findByID(rule.parameterValue.TopEdgeProduct);
                if (topEdgeProduct == null) {
                    rule.parameterValue.TopEdgeProduct = MaterialConfigurator.ZERO_GUID;
                }
            }
            if (rule.parameterValue.hasOwnProperty("BottomEdgeProduct")) {
                const bottomEdgeProduct: IEdgeData | null = Edges.instance!.findByID(rule.parameterValue.BottomEdgeProduct);
                if (bottomEdgeProduct == null) {
                    rule.parameterValue.BottomEdgeProduct = MaterialConfigurator.ZERO_GUID;
                }
            }
        }

        // assign ZERO_GUID if rule.value.EdgeProduct is empty
        let edgeProductID: string = MaterialConfigurator.ZERO_GUID;
        let edges: EdgesDescriptor = {};

        // if any of the edge products are set, then we will use them
        if (rule.parameterValue.hasOwnProperty("LeftEdgeProduct")) {
            edgeProductID = rule.parameterValue.LeftEdgeProduct != "" ? rule.parameterValue.LeftEdgeProduct : MaterialConfigurator.ZERO_GUID;
            edges[edgeProductID] = edges[edgeProductID] || [];
            edges[edgeProductID].push("L");
        }

        if (rule.parameterValue.hasOwnProperty("RightEdgeProduct")) {
            edgeProductID = rule.parameterValue.RightEdgeProduct != "" ? rule.parameterValue.RightEdgeProduct : MaterialConfigurator.ZERO_GUID;
            edges[edgeProductID] = edges[edgeProductID] || [];
            edges[edgeProductID].push("R");
        }

        if (rule.parameterValue.hasOwnProperty("TopEdgeProduct")) {
            edgeProductID = rule.parameterValue.TopEdgeProduct != "" ? rule.parameterValue.TopEdgeProduct : MaterialConfigurator.ZERO_GUID;
            edges[edgeProductID] = edges[edgeProductID] || [];
            edges[edgeProductID].push("T");
        }

        if (rule.parameterValue.hasOwnProperty("BottomEdgeProduct")) {
            edgeProductID = rule.parameterValue.BottomEdgeProduct != "" ? rule.parameterValue.BottomEdgeProduct : MaterialConfigurator.ZERO_GUID;
            edges[edgeProductID] = edges[edgeProductID] || [];
            edges[edgeProductID].push("B");
        }

        for (let edgeID in edges) {
            this.createEdgeProduct(edges, edgeID);
        }

        this.pimpCheckBoxes();
    }

    // TODO: create paramscript interface type
    public createParamScript(): Object {
        const res: any = {};
        // DisplayName can be: BaseMaterial, FrontMaterial, BackMaterial, ShelfMaterial, DrawerMaterial
        res[this.rule.title] = { "Decor": {} };

        if (this.rule.targetType !== null) {
            res[this.rule.title]["TargetType"] = this.rule.targetType;
        }

        if (this.rule.targetPath !== null) {
            res[this.rule.title]["Path"] = this.rule.targetPath;
        }

        // board product
        if (this.rule.parameterValue.BoardProduct) {
            res[this.rule.title]["Decor"]["BoardProduct"] = this.rule.parameterValue.BoardProduct;
        }

        if (this.rule.parameterValue.hasOwnProperty("BoardPatternDirection")) {
            res[this.rule.title]["Decor"]["BoardPatternDirection"] = this.rule.parameterValue.BoardPatternDirection;
        }

        if(this.boardProduct.duplungEnabled) {
            if (this.rule.parameterValue.hasOwnProperty("DuplungProduct")) {
                res[this.rule.title]["Decor"]["DuplungFixing"] = "Gluing"; //Gluing, Screwing
                res[this.rule.title]["Decor"]["DuplungProduct"] = this.rule.parameterValue.DuplungProduct;
                res[this.rule.title]["Decor"]["DuplungPatternDirection"] = this.rule.parameterValue.DuplungPatternDirection;
            }
        }

        if (this.boardProduct.edgebandable) {
            for (let index = 0; index < this.edgeProducts.length; index++) {
                for (let prefix of ["Left", "Right", "Top", "Bottom"]) {
                    const edgeKey: string = prefix + "EdgeProduct";

                    let edgeValue: string = "";
                    if (prefix === "Left") {
                        edgeValue = this.rule.parameterValue.LeftEdgeProduct;
                    } else if (prefix === "Right") {
                        edgeValue = this.rule.parameterValue.RightEdgeProduct;
                    } else if (prefix === "Top") {
                        edgeValue = this.rule.parameterValue.TopEdgeProduct;
                    } else if (prefix === "Bottom") {
                        edgeValue = this.rule.parameterValue.BottomEdgeProduct;
                    }

                    if (edgeValue === "") {
                        edgeValue = MaterialConfigurator.ZERO_GUID;
                    }
                    res[this.rule.title]["Decor"][edgeKey] = edgeValue;
                }
            }
        }
        if (this.edgeProducts.length === 0 || !this.boardProduct.edgebandable) {
            res[this.rule.title]["Decor"]["LeftEdgeProduct"] = MaterialConfigurator.ZERO_GUID;
            res[this.rule.title]["Decor"]["RightEdgeProduct"] = MaterialConfigurator.ZERO_GUID;
            res[this.rule.title]["Decor"]["TopEdgeProduct"] = MaterialConfigurator.ZERO_GUID;
            res[this.rule.title]["Decor"]["BottomEdgeProduct"] = MaterialConfigurator.ZERO_GUID;
        }

        return res;
    }

    private pimpCheckBoxes(): void {
        this.checkBoxRadio("T");
        this.checkBoxRadio("B");
        this.checkBoxRadio("L");
        this.checkBoxRadio("R");
    }

    private addEdgeProductSlot(): void {
        this.createEdgeProduct({}, MaterialConfigurator.ZERO_GUID);
        this.pimpCheckBoxes();
    }

    private createEdgeProduct(edgesObj: EdgesDescriptor, edgeID: string): void {
        if (this.edgeProducts.length < 4) {
            let ep: EdgeProduct = new EdgeProduct(edgesObj, edgeID);

            // we may have more than one edge products, but if one of them is ZERO_GUID,
            // then we should not create additional slot for it.
            if (Object.keys(edgesObj).length > 1) {
                if (edgeID == MaterialConfigurator.ZERO_GUID) {
                    return;
                }
            }

            // Newly added decor should check all unused checkboxes
            const edgeCombos: NodeListOf<HTMLElement> = this.container.querySelectorAll<HTMLElement>('[data-id="edge-selector-combo"]');
            if (edgeCombos.length > 0) {
                for (const letter of ["T", "B", "L", "R"]) {
                    let checkboxes: NodeListOf<HTMLInputElement> = this.container.querySelectorAll<HTMLInputElement>('[data-id="' + letter + '"]');
                    let anyChecked: boolean = false;
                    for (const cb of checkboxes) {
                        if (cb.checked) {
                            anyChecked = true;
                            break;
                        }
                    }

                    if (anyChecked === false) {
                        ep.getHTMLElement().querySelector<HTMLInputElement>('[data-id="' + letter + '"]')!.checked = true;
                    }
                }
            }

            this.edgeProducts.push(ep);

            ep.DecorSelectorCombo.addEventListener("changed", (e: Event) => {
                const event = e as CustomEvent<{ value: string }>;
                ep.edgeID = event.detail.value;
                console.log("combo value changed to: " + ep.edgeID);
                ep.updateThumbnail();
                // since the decor could be changed underneath the checkboxes, we must reassign the target edge ids
                this.assignAllEdgeIDs();

                this.setParameters();
            });

            // I have moved this outside because first we need to hide edges that are not suitable for the board,
            // compatible, but wider...
            // Use compatible edgings of the board to update the combo
            //const theBoard: IBoardData | null = Boards.instance!.findByID(this.boardProduct.boardID);
            //if (theBoard != null && theBoard.CompatibleEdgings != null) {
            //    ep.combo.rearrange(theBoard.CompatibleEdgings);
            //}

            if (edgeID === MaterialConfigurator.ZERO_GUID) {
                // Select the first item in the combo. Compatible edgings are moved to the top
                ep.combo.selectByIndex(0);
            } else {
                // Select the explicitly set edge
                ep.combo.selectByID(edgeID);
            }

            ep.TrashBtn.addEventListener("click", (event: Event) => {
                // first uncheck all checked in this card and trigger change event
                for (let cb of ep.getHTMLElement().querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
                    if (cb.checked) {
                        cb.checked = false;
                        cb.dispatchEvent(new Event("change"));
                    }
                }

                const idx: number = this.edgeProducts.indexOf(ep);
                this.edgeProducts.splice(idx, 1);

                ep.removeHTMLElement();
            });

            this.container.appendChild(ep.getHTMLElement());
        }
    }

    // will make checkboxes in group partially behave like radio buttons
    private checkBoxRadio(letter: string): void {

        let checkboxes: NodeListOf<HTMLInputElement> = this.container.querySelectorAll<HTMLInputElement>('[data-id="' + letter + '"]');

        for (const cb of checkboxes) {
            cb.addEventListener("change", (event: Event) => {
                if (cb.checked) {

                    // Uncheck other checkboxes in the group
                    for (let j = 0; j < checkboxes.length; j++) {
                        if (checkboxes[j] !== cb) {
                            checkboxes[j].checked = false;
                        }
                    }
                }
                this.assignAllEdgeIDs();
                this.setParameters();
            });
        }
    }

    private assignAllEdgeIDs(): void {
        // first, zero all target data
        this.assignEdgeID("T", MaterialConfigurator.ZERO_GUID);
        this.assignEdgeID("B", MaterialConfigurator.ZERO_GUID);
        this.assignEdgeID("L", MaterialConfigurator.ZERO_GUID);
        this.assignEdgeID("R", MaterialConfigurator.ZERO_GUID);

        for (const ep of this.edgeProducts) {
            const checkboxes: NodeListOf<HTMLInputElement> = ep.getHTMLElement().querySelectorAll<HTMLInputElement>('input[type="checkbox"]');

            for (const cb of checkboxes) {
                if (cb.checked) {
                    const letter: string | null = cb.getAttribute("data-id");
                    if (letter != null) {
                        this.assignEdgeID(letter, ep.edgeID);

                        console.log("Assigned edgeID: " + letter + ": " + ep.edgeID);
                    } else {
                        console.error("Missing data-id attribute in checkbox: " + cb);
                    }
                }
            }
        }
    }

    private assignEdgeID(letter: string, edgeID: string): void {
        switch (letter) {
            case "T": this.rule.parameterValue.TopEdgeProduct = edgeID;
                break;
            case "B": this.rule.parameterValue.BottomEdgeProduct = edgeID;
                break;
            case "L": this.rule.parameterValue.LeftEdgeProduct = edgeID;
                break;
            case "R": this.rule.parameterValue.RightEdgeProduct = edgeID;
                break;
            default: throw "Unhandled letter in Material configurator's assignEdgeID: " + letter + ": " + edgeID;
        }
    }

}

export default MaterialConfigurator;
